import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FloatingShapes from './components/ui/FloatingShapes';
import FloatingButtons from './components/ui/FloatingButtons';
import ProtectedRoute from './components/layout/ProtectedRoute';
// Pages
import HomePage from './pages/HomePage';
import CoursesPage from './pages/CoursesPage';
import ContactPage from './pages/ContactPage';
import CurrentAffairsPage from './pages/CurrentAffairsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import BlogsPage from './pages/BlogsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import OnlineClassPage from './pages/OnlineClassPage';
import DashboardPage from './pages/DashboardPage';
// CUET Mock Registration Pages
import CuetMockLayout from './pages/cuet-mock/CuetMockLayout';
import InstructionsPage from './pages/cuet-mock/InstructionsPage';
import RegistrationPage from './pages/cuet-mock/RegistrationPage';
import SuccessPage from './pages/cuet-mock/SuccessPage';
import CuetLoginPage from './pages/cuet-mock/LoginPage';
import CuetDashboard from './pages/cuet-mock/Dashboard';
import Step0Home from './pages/cuet-mock/steps/Step0Home';
import Step1Contact from './pages/cuet-mock/steps/Step1Contact';
import Step2Personal from './pages/cuet-mock/steps/Step2Personal';
import Step3PWD from './pages/cuet-mock/steps/Step3PWD';
import Step4Identity from './pages/cuet-mock/steps/Step4Identity';
import Step5Apaar from './pages/cuet-mock/steps/Step5Apaar';
import Step6ExamCenter from './pages/cuet-mock/steps/Step6ExamCenter';
import Step7Qualification from './pages/cuet-mock/steps/Step7Qualification';
import Step8Additional from './pages/cuet-mock/steps/Step8Additional';
import Step9Emergency from './pages/cuet-mock/steps/Step9Emergency';
import Step10PhotoCapture from './pages/cuet-mock/steps/Step10PhotoCapture';
import Step11Upload from './pages/cuet-mock/steps/Step11Upload';
import Step12FinalSubmit from './pages/cuet-mock/steps/Step12FinalSubmit';
// Test
import TestZonePage from './pages/TestZonePage';
import TestEnginePage from './pages/TestEnginePage';
import TestEngineMockPage from './pages/TestEngineMockPage';
import TestResultPage from './pages/TestResultPage';
import TestReviewPage from './pages/TestReviewPage';
import SearchColleges from './pages/SearchColleges';
import PerformancePage from './pages/PerformancePage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';

// Landing Pages for Services
import CuetCounsellingPage from './pages/CuetCounsellingPage';
import StudyMaterialsPage from './pages/StudyMaterialsPage';
import CurrentAffairsTestPage from './pages/CurrentAffairsTestPage';
import DigitalMarketingPage from './pages/DigitalMarketingPage';
import ReasoningTestPage from './pages/ReasoningTestPage';
import UgcNetMockPage from './pages/UgcNetMockPage';
import NewsUpdatesPage from './pages/NewsUpdatesPage';
import PYQCuetPage from './pages/PYQCuetPage';
import SelfPacedPage from './pages/SelfPacedPage';
import CuetPgCounsellingPage from './pages/CuetPgCounsellingPage';

import CuetUgMockTestPage from './pages/CuetUgMockTestPage';
import CurrentAffairsTestEngine from './pages/CurrentAffairsTestEngine';
import CuetUgPage from './pages/CuetUgPage';
import CuetPgPage from './pages/CuetPgPage';
import NetJrfPage from './pages/NetJrfPage';
import FaqPage from './pages/FaqPage';
import PurchaseHistoryPage from './pages/PurchaseHistoryPage';
import StudentStudyMaterialsPage from './pages/StudentStudyMaterialsPage';
import StudentPYQPage from './pages/StudentPYQPage';
import QuestionBankPage from './pages/QuestionBankPage';
import QuestionDetailPage from './pages/QuestionDetailPage';

import CuetHubPage from './pages/CuetHubPage';
import CuetCoachingPage from './pages/seo-blogs/pillars/1/CuetCoachingPage';
import BestCuetCoachingAssamPage from './pages/seo-blogs/pillars/1/BestCuetCoachingAssamPage';
import OnlineCuetCoachingPage from './pages/seo-blogs/pillars/1/OnlineCuetCoachingPage';
import CuetPreparationStrategyPage from './pages/seo-blogs/pillars/1/CuetPreparationStrategyPage';
import HowToCrackCuetPage from './pages/seo-blogs/pillars/1/HowToCrackCuetPage';
import CuetStudyPlanPage from './pages/seo-blogs/pillars/1/CuetStudyPlanPage';

// Context
import { AuthProvider } from './context/AuthContext';
import { HelmetProvider, Helmet } from 'react-helmet-async';
// Placeholder component for service pages
const ServicePage = ({ title }) => (
  <div className="min-h-screen pt-32 pb-20">
    <div className="max-w-[1400px] mx-auto px-8 text-center">
      <h1 className="text-4xl font-bold gradient-text mb-6">{title}</h1>
      <p className="text-text-muted text-xl">This page is coming soon...</p>
    </div>
  </div>
);

/**
 * FIX: ScrollToTop Component
 * This resets the window scroll position to (0,0) every time the URL path changes.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Fires fbq('track', 'PageView') on every route change.
const FbPixelTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);
  return null;
};

// Layout Wrapper
const Layout = ({ children }) => {
  const location = useLocation();
  const isDashboard = ['/dashboard', '/test-zone', '/performance', '/settings', '/purchase-history', '/my-study-materials', '/my-pyq'].some(p => location.pathname.startsWith(p));
  return (
    <div className="min-h-screen flex flex-col">
      <FloatingShapes />
      <Header />
      <main className="flex-1">
        {children}
      </main>
      {!isDashboard && <Footer />}
      <FloatingButtons />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <HelmetProvider>
        <Helmet>
          <meta name="description" content="BESST - Brahmaputra Exam Success Support Team. India's leading online coaching platform for CUET, competitive exams, and academic excellence." />
        </Helmet>
        <Router>
          {/* Helper components inside Router */}
          <ScrollToTop />
          <FbPixelTracker />

          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/current-affairs" element={<CurrentAffairsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/blogs" element={<BlogsPage />} />
              <Route path="/subscriptions" element={<SubscriptionPage />} />

              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/search-colleges" element={<ProtectedRoute><SearchColleges /></ProtectedRoute>} />
              <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
              <Route path="/purchase-history" element={<ProtectedRoute><PurchaseHistoryPage /></ProtectedRoute>} />
              <Route path="/my-study-materials" element={<ProtectedRoute><StudentStudyMaterialsPage /></ProtectedRoute>} />
              <Route path="/my-pyq" element={<ProtectedRoute><StudentPYQPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />

              {/* Landing Routes */}
              <Route path="/cuet-counselling" element={<CuetCounsellingPage />} />
              <Route path="/study-materials" element={<StudyMaterialsPage />} />
              <Route path="/current-affairs-test" element={<CurrentAffairsTestPage />} />
              <Route path="/digital-marketing" element={<DigitalMarketingPage />} />
              <Route path="/reasoning-test" element={<ReasoningTestPage />} />
              <Route path="/ugc-net-mock" element={<UgcNetMockPage />} />
              <Route path="/news-updates" element={<NewsUpdatesPage />} />
              <Route path="/pyq-cuet" element={<PYQCuetPage />} />
              <Route path="/self-paced" element={<SelfPacedPage />} />
              <Route path="/cuet-pg-counselling" element={<CuetPgCounsellingPage />} />

              <Route path="/cuet-ug-mock-test" element={<CuetUgMockTestPage />} />
              <Route path="/current-affairs-quiz" element={<CurrentAffairsTestEngine />} />
              <Route path="/cuet-ug" element={<CuetUgPage />} />
              <Route path="/cuet-pg" element={<CuetPgPage />} />
              <Route path="/net-jrf" element={<NetJrfPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/question-bank" element={<QuestionBankPage />} />
              <Route path="/questions/:slug" element={<QuestionDetailPage />} />
              <Route path="/blog" element={<CuetHubPage />} />
              <Route path="/cuet-coaching" element={<CuetCoachingPage />} />
              <Route path="/best-cuet-coaching-in-assam" element={<BestCuetCoachingAssamPage />} />
              <Route path="/online-cuet-coaching" element={<OnlineCuetCoachingPage />} />
              <Route path="/cuet-preparation-strategy" element={<CuetPreparationStrategyPage />} />
              <Route path="/how-to-crack-cuet" element={<HowToCrackCuetPage />} />
              <Route path="/cuet-study-plan" element={<CuetStudyPlanPage />} />

              {/* Service Routes */}
              <Route path="/services/online-classes" element={<OnlineClassPage title="Online Classes" />} />
              <Route path="/services/mock-test-series" element={<ServicePage title="Mock Test Series" />} />
              <Route path="/services/study-materials" element={<ServicePage title="Downloadable Study Materials" />} />
              <Route path="/services/question-bank" element={<ServicePage title="Previous Year Question Bank" />} />
              <Route path="/services/counselling" element={<ServicePage title="Counselling and Mock Registration" />} />
              <Route path="/services/du-guidance" element={<ServicePage title="Delhi University Admission Guidance" />} />

              {/* Test Routes */}
              <Route path="/test-zone" element={<ProtectedRoute><TestZonePage /></ProtectedRoute>} />
              <Route path="/test-engine/:quizId" element={<ProtectedRoute><TestEnginePage /></ProtectedRoute>} />
              <Route path="/test-engine-mock/:quizId" element={<ProtectedRoute><TestEngineMockPage /></ProtectedRoute>} />
              <Route path="/test-review" element={<ProtectedRoute><TestReviewPage /></ProtectedRoute>} />
              <Route path="/test-review/:quizId" element={<ProtectedRoute><TestReviewPage /></ProtectedRoute>} />
              <Route path="/test-result" element={<ProtectedRoute><TestResultPage /></ProtectedRoute>} />

              {/* Mock Registration Routes */}
              <Route path="/cuet-mock" element={<ProtectedRoute><CuetMockLayout /></ProtectedRoute>}>
                <Route index element={<InstructionsPage />} />
                <Route path="registration" element={<RegistrationPage />} />
                <Route path="success" element={<SuccessPage />} />
                <Route path="login" element={<CuetLoginPage />} />
                <Route path="dashboard" element={<CuetDashboard />}>
                  <Route index element={<Step0Home />} />
                  <Route path="contact-details" element={<Step1Contact />} />
                  <Route path="personal-details" element={<Step2Personal />} />
                  <Route path="pwd-details" element={<Step3PWD />} />
                  <Route path="identity-details" element={<Step4Identity />} />
                  <Route path="apaar-id" element={<Step5Apaar />} />
                  <Route path="exam-center" element={<Step6ExamCenter />} />
                  <Route path="qualification-details" element={<Step7Qualification />} />
                  <Route path="additional-details" element={<Step8Additional />} />
                  <Route path="emergency-contact" element={<Step9Emergency />} />
                  <Route path="photo-capture" element={<Step10PhotoCapture />} />
                  <Route path="upload-documents" element={<Step11Upload />} />
                  <Route path="final-submit" element={<Step12FinalSubmit />} />
                </Route>
              </Route>

              <Route path="*" element={<div className="text-center mt-20 pt-20">404 - Page Not Found</div>} />
            </Routes>
          </Layout>
        </Router>
      </HelmetProvider>
    </AuthProvider>
  );
}

export default App;